/**
 * EventsComponent — Composant de consultation du journal des événements système (c_event)
 *
 * Appelé depuis le menu "Paramètres > Events" du module Ventes (/sales/events).
 * Dépendances : EventsService, CommonService (employés), AlertService, SelectDateComponent, MenuCommonComponent.
 * Fournit une interface de recherche avancée inspirée de quotations.component.ts avec
 * visualisation détaillée du payload c_event.data dans un accordéon et rechargement unitaire via pullevent.
 */

import { Component, OnInit, AfterViewInit, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule, MatAccordion } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { CommonService } from '@app/common/common.service';
import { AlertService } from '@app/shared/alert.service';
import { User } from '@app/shared/_models/common';
import { ObjectType, EventType } from '@app/shared/constants-server';

import { EventsService, EventItem, EventsFilter } from './events.service';

@Component({
  selector: 'app-events',
  standalone: true,
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    DatePipe,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    MenuCommonComponent,
    SelectDateComponent,
  ],
})
export class EventsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatAccordion) accordion?: MatAccordion;

  /** Liste des événements chargés */
  public events: EventItem[] = [];

  /** Indicateur de chargement global */
  public is_loading = false;

  /** Liste des utilisateurs pour le filtre opérateur */
  public users: User[] = [{ id: -1, store: -1, currentstore: 0, firstname: 'Tous' }];

  /** Limites configurables (par défaut : 10) */
  public readonly limitsValue = ['10', '25', '50', '100', '250', '500', '1000'];

  /** Référentiel des types d'objets pour le filtre */
  public readonly objectTypes = [
    { id: -1, label: 'Tous les objets' },
    { id: ObjectType.ORDER, label: '3 - Commande (ORDER)' },
    { id: ObjectType.INVOICE, label: '5 - Facture (INVOICE)' },
    { id: ObjectType.PARTNER, label: '1 - Partenaire / Client (PARTNER)' },
    { id: ObjectType.PRODUCT, label: '6 - Produit (PRODUCT)' },
    { id: ObjectType.USER, label: '8 - Utilisateur (USER)' },
    { id: ObjectType.PERIOD, label: '12 - Période caisse (PERIOD)' },
    { id: ObjectType.PAYMENT, label: '15 - Paiement (PAYMENT)' },
    { id: ObjectType.DELIVERY, label: '4 - Livraison (DELIVERY)' },
    { id: ObjectType.DELRUN, label: '24 - Tournée livraison (DELRUN)' },
    { id: ObjectType.HR_EMPLOYEE, label: '25 - Salarié (HR_EMPLOYEE)' },
    { id: ObjectType.HR_CLOCK, label: '28 - Pointage RH (HR_CLOCK)' },
    { id: ObjectType.HR_SCHEDULE, label: '26 - Planning RH (HR_SCHEDULE)' },
    { id: ObjectType.ATTACHEMENT, label: '19 - Pièce jointe (ATTACHEMENT)' },
  ];

  /** Référentiel des types d'événements pour le filtre */
  public readonly eventTypes = [
    { id: -1, label: 'Toutes les actions' },
    { id: EventType.INSERT, label: '1 - INSERT' },
    { id: EventType.UPDATE, label: '2 - UPDATE' },
    { id: EventType.DELETE, label: '3 - DELETE' },
    { id: EventType.PRINT, label: '4 - PRINT' },
    { id: EventType.CHANGE_ORDER_STATE, label: '100 - CHANGE_ORDER_STATE' },
    { id: EventType.INVOICING, label: '101 - INVOICING' },
    { id: EventType.USER_AUTHENTICATE, label: '102 - USER_AUTHENTICATE' },
    { id: EventType.PAYMENT, label: '103 - PAYMENT' },
    { id: EventType.TAKE_DEPOSIT, label: '104 - TAKE_DEPOSIT' },
    { id: EventType.SAFE_INVOICING, label: '200 - SAFE_INVOICING' },
    { id: EventType.DELIVERY_VALIDATE_AND_PRINT, label: '701 - DELIVERY_VALIDATE' },
    { id: EventType.PERIOD_OPEN, label: '1201 - PERIOD_OPEN' },
    { id: EventType.PERIOD_CLOSE, label: '1202 - PERIOD_CLOSE' },
    { id: EventType.HR_CLOCK_PUNCH, label: '2801 - HR_CLOCK_PUNCH' },
  ];

  /** Filtre d'origine avec limite par défaut = 10 */
  public readonly originalFilter: EventsFilter = {
    id: '',
    objectid: '',
    limit: '10',
    objecttyp: '-1',
    typ: '-1',
    userid: '-1',
    datstart: (() => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      return d;
    })(),
    datend: new Date(),
  };

  /** Filtre actif */
  public filter: EventsFilter = { ...this.originalFilter };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private eventsService: EventsService,
    private commonService: CommonService,
    private alertService: AlertService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.alertService.clear();

    // Chargement de la liste des utilisateurs pour le filtre
    this.commonService
      .getUsers()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (users: User[]) => {
          this.users = [{ id: -1, store: -1, currentstore: 0, firstname: 'Tous' }, ...users];
          this.cdr.markForCheck();
        },
        error: (err) => console.error('Erreur chargement utilisateurs:', err),
      });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.onSearch());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Lance la recherche des événements selon les filtres courants.
   */
  onSearch(): void {
    this.is_loading = true;
    this.cdr.markForCheck();

    this.eventsService
      .getEvents(this.filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (items: EventItem[]) => {
          this.events = items;
          this.is_loading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.is_loading = false;
          const msg = err?.error || err?.message || 'Erreur lors du chargement des événements';
          this.alertService.error(`Erreur Serveur: ${msg}`);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Réinitialise les filtres à leurs valeurs par défaut (limit = 10).
   */
  onCancel(): void {
    this.filter = { ...this.originalFilter };
    this.onSearch();
  }

  /**
   * Gestion du changement de date.
   */
  dateChange(date: any, target: 'datstart' | 'datend'): void {
    this.filter[target] = date;
    this.onSearch();
  }

  /**
   * Relit un événement via l'endpoint dédié `pullevent(id)`.
   * Permet de rafraîchir à la demande le payload c_event.data d'une ligne d'accordéon.
   *
   * @param event L'événement à recharger
   * @param mouseEvent Événement de clic optionnel pour empêcher la fermeture du panel
   */
  onPullEvent(event: EventItem, mouseEvent?: MouseEvent): void {
    if (mouseEvent) {
      mouseEvent.stopPropagation();
    }

    event._reloading = true;
    this.cdr.markForCheck();

    this.eventsService
      .pullEvent(event.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (reloaded: EventItem) => {
          event.data = reloaded.data;
          event.datpro = reloaded.datpro;
          event.result = reloaded.result;
          event._reloading = false;
          this.alertService.success(`Événement #${event.id} rechargé avec succès (pullevent)`);
          this.cdr.markForCheck();
        },
        error: (err) => {
          event._reloading = false;
          const msg = err?.error || err?.message || 'Erreur pullevent';
          this.alertService.error(`Échec rechargement événement #${event.id}: ${msg}`);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Copie le contenu JSON de c_event.data dans le presse-papiers.
   *
   * @param event L'événement dont on copie le payload
   * @param mouseEvent Événement de clic optionnel
   */
  copyJson(event: EventItem, mouseEvent?: MouseEvent): void {
    if (mouseEvent) {
      mouseEvent.stopPropagation();
    }

    try {
      const jsonStr = JSON.stringify(event.data, null, 2);
      navigator.clipboard.writeText(jsonStr).then(() => {
        event._copied = true;
        this.cdr.markForCheck();
        setTimeout(() => {
          event._copied = false;
          this.cdr.markForCheck();
        }, 2000);
      });
    } catch (e) {
      console.error('Erreur copie presse-papiers:', e);
    }
  }

  /**
   * Déplie tous les panneaux de l'accordéon.
   */
  expandAll(): void {
    this.accordion?.openAll();
  }

  /**
   * Replie tous les panneaux de l'accordéon.
   */
  collapseAll(): void {
    this.accordion?.closeAll();
  }

  /**
   * Formate le JSON pour affichage indenté dans la balise `<pre>`.
   */
  formatJson(data: any): string {
    if (data === null || data === undefined) {
      return 'null';
    }
    return JSON.stringify(data, null, 2);
  }

  /**
   * Retourne un aperçu synthétique compact du payload pour l'entête du panneau.
   */
  getPayloadSummary(event: EventItem): string {
    if (!event.data || typeof event.data !== 'object') {
      return '';
    }
    const d = event.data;
    const parts: string[] = [];

    if (d.doc_type !== undefined) parts.push(`type=${d.doc_type}`);
    if (d.state !== undefined) parts.push(`state=${d.state}`);
    if (d.status !== undefined) parts.push(`status=${d.status}`);
    if (d.login !== undefined) parts.push(`login=${d.login}`);
    if (d.total !== undefined) parts.push(`tot=${d.total}€`);
    if (d.amount !== undefined) parts.push(`mnt=${d.amount}€`);

    if (parts.length > 0) {
      return parts.join(' | ');
    }

    const keys = Object.keys(d);
    if (keys.length === 0) return 'vide';
    return keys.slice(0, 3).join(', ') + (keys.length > 3 ? '…' : '');
  }

  /**
   * Traduit l'identifiant numérique de l'objet en libellé lisible.
   */
  getObjectTypeLabel(objecttyp: number): string {
    switch (objecttyp) {
      case ObjectType.ORDER:
        return 'COMMANDE';
      case ObjectType.INVOICE:
        return 'FACTURE';
      case ObjectType.PARTNER:
        return 'CLIENT/TIERS';
      case ObjectType.PRODUCT:
        return 'PRODUIT';
      case ObjectType.USER:
        return 'UTILISATEUR';
      case ObjectType.PERIOD:
        return 'PÉRIODE CAISSE';
      case ObjectType.PAYMENT:
        return 'PAIEMENT';
      case ObjectType.DELIVERY:
        return 'LIVRAISON';
      case ObjectType.DELRUN:
        return 'TOURNÉE';
      case ObjectType.HR_EMPLOYEE:
        return 'SALARIÉ RH';
      case ObjectType.HR_CLOCK:
        return 'POINTAGE RH';
      case ObjectType.HR_SCHEDULE:
        return 'PLANNING RH';
      case ObjectType.ATTACHEMENT:
        return 'PIÈCE JOINTE';
      default:
        return `OBJET #${objecttyp}`;
    }
  }

  /**
   * Classe CSS de couleur pour le badge objet.
   */
  getObjectBadgeClass(objecttyp: number): string {
    switch (objecttyp) {
      case ObjectType.ORDER:
        return 'badge-order';
      case ObjectType.INVOICE:
        return 'badge-invoice';
      case ObjectType.PARTNER:
        return 'badge-partner';
      case ObjectType.PRODUCT:
        return 'badge-product';
      case ObjectType.USER:
        return 'badge-user';
      case ObjectType.PERIOD:
        return 'badge-period';
      case ObjectType.PAYMENT:
        return 'badge-payment';
      default:
        return 'badge-default';
    }
  }

  /**
   * Traduit le type d'action en libellé lisible.
   */
  getEventTypeLabel(typ: number): string {
    switch (typ) {
      case EventType.INSERT:
        return 'INSERT';
      case EventType.UPDATE:
        return 'UPDATE';
      case EventType.DELETE:
        return 'DELETE';
      case EventType.PRINT:
        return 'PRINT';
      case EventType.CHANGE_ORDER_STATE:
        return 'CHANGEMENT ÉTAT';
      case EventType.INVOICING:
        return 'FACTURATION';
      case EventType.USER_AUTHENTICATE:
        return 'LOGIN / AUTH';
      case EventType.PAYMENT:
        return 'RÈGLEMENT';
      case EventType.TAKE_DEPOSIT:
        return 'ACOMPTE';
      case EventType.SAFE_INVOICING:
        return 'SCELLEMENT FISCAL';
      case EventType.DELIVERY_VALIDATE_AND_PRINT:
        return 'VALID. LIVRAISON';
      case EventType.PERIOD_OPEN:
        return 'OUVERTURE CAISSE';
      case EventType.PERIOD_CLOSE:
        return 'CLÔTURE CAISSE';
      case EventType.HR_CLOCK_PUNCH:
        return 'POINTAGE';
      default:
        return `ACTION #${typ}`;
    }
  }

  /**
   * Classe CSS de couleur pour le badge action.
   */
  getEventBadgeClass(typ: number): string {
    switch (typ) {
      case EventType.INSERT:
        return 'badge-success';
      case EventType.UPDATE:
        return 'badge-info';
      case EventType.DELETE:
        return 'badge-error';
      case EventType.PRINT:
        return 'badge-secondary';
      case EventType.CHANGE_ORDER_STATE:
        return 'badge-accent';
      case EventType.INVOICING:
      case EventType.SAFE_INVOICING:
        return 'badge-warning';
      case EventType.USER_AUTHENTICATE:
        return 'badge-neutral';
      default:
        return 'badge-default';
    }
  }
}
